import type { DataType } from "@repo/lanes";

import type { OperationType } from "../operation-types";
import { AGG_DELIMITER, type RiftConfig } from "../types";
import { defaultAggForType } from "./default-agg-for-type";
import { isNumericOperation } from "./operation-output-type";

/** A projected column: its (possibly post-aggregation) name and data type. */
export type Column = { name: string; type: string };

/**
 * Resolves the data type an aggregate output column has, given the operation and
 * the input column's type. Mirrors `operationOutputType` but tolerates the
 * `"unknown"` placeholder used when an input column's type is not known.
 */
const aggregatedType = (
  op: OperationType,
  inputType: string | undefined,
): string => {
  if (op === "CONCAT") return "STRING";
  if (isNumericOperation(op)) return "NUMBER";
  // MIN / MAX (and any other value-preserving op) keep the input type.
  return inputType ?? "unknown";
};

/**
 * Computes the column list a chart/table sees after the base `rift()` SQL
 * runs. Mirrors the SELECT shape produced by `packages/shared/rift/rift.ts`:
 * - No groups + no aggregates → all input columns minus `hiddenColumns`.
 * - Groups present → group columns keep their original names; single-op
 *   aggregates (and `CONCAT`) keep the original column name; multi-op
 *   aggregates use the `${col}::${op}` alias per op; non-grouped,
 *   non-explicitly-aggregated input columns are auto-aggregated under their
 *   original names.
 * - Aggregates without groups → the aggregate columns (raw name for single
 *   op, `${col}::${op}` for multi-op).
 *
 * The output type reflects the aggregation: numeric ops produce `NUMBER`,
 * `CONCAT` produces `STRING`, and `MIN` / `MAX` preserve the input type — all
 * via the shared `operationOutputType` classification so the agent, the chart
 * axis pickers, and the chart SQL agree. Auto-aggregated columns are typed by
 * their `defaultAggForType` operation, which is what the engine emits for them.
 */
export const projectColumns = (
  riftConfig: Partial<RiftConfig> | null | undefined,
  inputColumns: Column[],
): Column[] => {
  if (!riftConfig) return inputColumns.slice();

  const hiddenSet = new Set(riftConfig.hiddenColumns ?? []);
  const groupAccessors = Object.entries(riftConfig.groups ?? {})
    .filter(([, on]) => on)
    .map(([k]) => k);
  const explicitAggEntries = Object.entries(riftConfig.aggregates ?? {}).filter(
    ([, ops]) => ops?.length,
  );

  const hasGroups = groupAccessors.length > 0;
  const hasAggregates = explicitAggEntries.length > 0;

  if (!hasGroups && !hasAggregates) {
    return inputColumns.filter((c) => !hiddenSet.has(c.name));
  }

  const inputByName = new Map(inputColumns.map((c) => [c.name, c]));
  const out: Column[] = [];
  const seen = new Set<string>();

  for (const accessor of groupAccessors) {
    if (seen.has(accessor)) continue;
    const input = inputByName.get(accessor);
    out.push({ name: accessor, type: input?.type ?? "unknown" });
    seen.add(accessor);
  }

  const groupSet = new Set(groupAccessors);
  const explicitAggSet = new Set(explicitAggEntries.map(([k]) => k));

  for (const [accessor, ops] of explicitAggEntries) {
    if (hiddenSet.has(accessor)) continue;
    // A grouped column is a dimension, not a measure — it is emitted by the
    // group loop above, never aggregated (mirrors rift.ts).
    if (groupSet.has(accessor)) continue;
    // Single-op aggregates and CONCAT keep the raw column name; only multi-op
    // aggregates use the `col::OP` alias to disambiguate the extra columns.
    const single = ops.length === 1;
    for (const op of ops) {
      if (op === "CONCAT" || single) {
        if (seen.has(accessor)) continue;
        const input = inputByName.get(accessor);
        out.push({ name: accessor, type: aggregatedType(op, input?.type) });
        seen.add(accessor);
      } else {
        const alias = `${accessor}${AGG_DELIMITER}${op}`;
        if (seen.has(alias)) continue;
        const input = inputByName.get(accessor);
        out.push({ name: alias, type: aggregatedType(op, input?.type) });
        seen.add(alias);
      }
    }
  }

  if (hasGroups) {
    for (const col of inputColumns) {
      if (groupSet.has(col.name)) continue;
      if (explicitAggSet.has(col.name)) continue;
      if (hiddenSet.has(col.name)) continue;
      if (seen.has(col.name)) continue;
      // Auto-aggregated: the engine applies the type default, so the output
      // type is that operation's output (e.g. STRING → COUNT → NUMBER), not
      // the input type.
      const op = defaultAggForType(col.type as DataType);
      out.push({ name: col.name, type: aggregatedType(op, col.type) });
      seen.add(col.name);
    }
  }

  return out;
};

/**
 * Convenience wrapper over `projectColumns` for client consumers that hold a
 * `columnDefs` map (column name → `DataType`). Returns the post-rift output
 * column types keyed by their (possibly aggregated) name, so chart axis pickers
 * and chart SQL transforms can ask "is this column numeric after the rift?"
 * from the same source of truth as the agent.
 */
export const riftOutputColumnTypes = (
  riftConfig: Partial<RiftConfig> | null | undefined,
  columnDefs: Record<string, DataType> | null | undefined,
): Record<string, DataType> => {
  const inputColumns: Column[] = Object.entries(columnDefs ?? {}).map(
    ([name, type]) => ({ name, type }),
  );
  const projected = projectColumns(riftConfig, inputColumns);
  const out: Record<string, DataType> = {};
  for (const col of projected) {
    out[col.name] = col.type as DataType;
  }
  return out;
};
